const interpolateDefault = interpolateFactory();
const interpolateClear = interpolateFactory({defaultReplacer: ""});

export {
  interpolateDefault as default,
  interpolateClear,
  addSymbolicStringExtensions,
  interpolateFactory,
};

/**
 * Factory function to create an interpolate function with a default replacer.
 * @param specs {defaultReplacer: string|number|undefined, useSymbolicExtensions: boolean|undefined}
 * @returns {function(*, ...[*]): string}
 */
function interpolateFactory(specs = {}) {
  let {defaultReplacer, useSymbolicExtensions} = specs;
  defaultReplacer = isStringOrNumber(defaultReplacer)
    ? String(defaultReplacer) : undefined;
  
  if (typeof useSymbolicExtensions === "boolean" && useSymbolicExtensions) {
    addSymbolicStringExtensions();
  }
  
  /**
   * Main interpolation function.
   * @param {string} str - The string with placeholders.
   * @param {...object} tokens - Objects containing replacement values.
   * @returns {string} - The interpolated string.
   */
  return function(str, ...tokens) {
    return interpolate(str, processTokens(tokens));
  }
  
  /**
   * Handle invalid keys by returning the default replacer or the key in braces.
   * @param {string} key - The placeholder key.
   * @param {boolean} keyExists - Flag indicating if the key exists in the token.
   * @returns {string} - The replacement value.
   */
  function invalidate(key, keyExists) {
    return keyExists && typeof defaultReplacer === `string`
      ? String(defaultReplacer) : `{${key}}`;
  }
  
  /**
   * Get the replacement value for a key from the token.
   * @param {string} key - The placeholder key.
   * @param {object} token - The token object containing replacement values.
   * @returns {string} - The replacement value.
   */
  function replacement(key, token) {
    const isValid = Object.hasOwn(token, key);
    return isValid && isStringOrNumber(token[key])
      ? String(token[key]) : invalidate(key, isValid);
    
  }
  
  /**
   * Create a lambda function for replacing placeholders in the string.
   * @param {object} token - The token object containing replacement values.
   * @returns {Function} - The replacer lambda function.
   */
  function getReplacerLambda(token) {
    return (...args) => {
      const replacementObject = args.find(a => a.key);
      return replacement((replacementObject ? replacementObject.key : `_`), token);
    };
  }
  
  /**
   * Replace placeholders in the string with values from the token.
   * @param {string} str - The string with placeholders.
   * @param {object} token - The token object containing replacement values.
   * @returns {string} - The interpolated string.
   */
  function replace(str, token) {
    return str.replace(/\{(?<key>[a-z_\d]+)}/gim, getReplacerLambda(token));
  }
  
  /**
   * Convert token object to array of token Objects
   * when it's values are arrays of values.
   * @param {object} tokenObject - The token object containing arrays of values.
   * @returns {object[]} - Array of token objects.
   */
  function convertTokensFromArrayValues(tokenObject) {
    const converted = [];
    
    Object.entries(tokenObject).forEach(([key, value]) => {
      value.forEach((v, i) => (converted[i] ??= {}, converted[i][key] = v));
    });
    
    return converted;
  }
  
  /**
   * Check if single token and its values are arrays.
   * @param {object[]} tokens - The tokens to check.
   * @returns {boolean} - True if tokens contains one Object
   *  and all it's values are of type Array.
   */
  function isMultiLineWithArrays(tokens) {
    return tokens.length === 1 && Object.values(tokens[0]).every(Array.isArray);
  }
  
  /**
   * Process tokens to handle multi-line formats.
   * @param {object[]} tokens - The tokens to process.
   * @returns {object[]} - Processed tokens.
   */
  function processTokens(tokens) {
    return isMultiLineWithArrays(tokens) ? convertTokensFromArrayValues(tokens[0]) : tokens;
  }
  
  /**
   * determine if [value] is a string or number
   * @param value
   * @returns {boolean}
   */
  function isStringOrNumber(value) {
    return typeof value === 'string' || typeof value === 'number';
  }
  
  /**
   * determine if [value] is a key-value collection
   * @param value
   * @returns {boolean}
   */
  function isKeyValueCollection(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }
  
  /**
   * Interpolate the string with the given tokens.
   * @param {string} str - The string with placeholders.
   * @param {object[]} tokens - The tokens containing replacement values.
   * @returns {string} - The interpolated string.
   */
  function interpolate(str, tokens) {
    const injected = !tokens?.length ? str : tokens
      .filter(token => isKeyValueCollection(token))
      .map((token, i) => replace(str, {...token, index: i + 1}))
      .join(``);
    
    return typeof defaultReplacer !== `string`
      ? injected : injected.replace(/\{.+}/gmi, defaultReplacer ?? ``);
  }
}

/**
 * Extend String.prototype using the above two
 * interpolate methods.
 * Note: Symbols are unique, so there is no risk the
 * methods will conflict with native String methods or
 * methods in other ES libraries.
 * @returns {symbol[]}
 */
function addSymbolicStringExtensions() {
  if (!String.prototype[Symbol.for(`interpolate`)]) {
    Object.defineProperties(String.prototype, {
      [Symbol.for(`interpolate`)]: {
        value(...args) {
          return interpolateDefault(this, ...args);
        }
      },
      [Symbol.for(`interpolate$`)]: {
        value(...args) {
          return interpolateClear(this, ...args);
        }
      },
    });
  }
  
  return [Symbol.for("interpolate"), Symbol.for(`interpolate$`)];
}
